import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Package, Users, Grid3x3, LogOut, LayoutDashboard, Plus } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { useState, useEffect } from 'react';
import { notificationsAPI } from '../utils/api';

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const [countRes, notifRes] = await Promise.all([
        notificationsAPI.getUnreadCount(),
        notificationsAPI.getAll()
      ]);
      setUnreadCount(countRes.data.count);
      setNotifications(notifRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      await notificationsAPI.markRead(notification.id);
      if (notification.link) {
        navigate(notification.link);
      }
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img 
                src="/consortium-logo.jpg" 
                alt="Consortium Gifts" 
                className="h-14 w-auto"
              />
            </Link>
            
            <div className="hidden md:flex items-center gap-1">
              <Link to="/dashboard">
                <Button
                  variant="ghost"
                  className={`gap-2 ${isActive('/dashboard') ? 'bg-consortium-green-light text-consortium-green' : 'text-slate-600'}`}
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/catalogue">
                <Button
                  variant="ghost"
                  className={`gap-2 ${isActive('/catalogue') ? 'bg-consortium-green-light text-consortium-green' : 'text-slate-600'}`}
                  data-testid="nav-catalogue"
                >
                  <Grid3x3 className="w-4 h-4" />
                  Catalogue
                </Button>
              </Link>
              <Link to="/collections">
                <Button
                  variant="ghost"
                  className={`gap-2 ${isActive('/collections') ? 'bg-consortium-green-light text-consortium-green' : 'text-slate-600'}`}
                  data-testid="nav-collections"
                >
                  <Package className="w-4 h-4" />
                  Collections
                </Button>
              </Link>
              <Link to="/proposal">
                <Button
                  variant="ghost"
                  className={`gap-2 relative ${isActive('/proposal') ? 'bg-consortium-green-light text-consortium-green' : 'text-slate-600'}`}
                  data-testid="nav-proposal"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Proposal
                </Button>
              </Link>
              {isAdmin() && (
                <Link to="/users">
                  <Button
                    variant="ghost"
                    className={`gap-2 ${isActive('/users') ? 'bg-consortium-green-light text-consortium-green' : 'text-slate-600'}`}
                    data-testid="nav-users"
                  >
                    <Users className="w-4 h-4" />
                    Users
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isAdmin() && (
              <>
                <Link to="/bulk-import">
                  <Button variant="outline" className="gap-2" data-testid="nav-bulk-import">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Bulk Import
                  </Button>
                </Link>
                <Link to="/products/new">
                  <Button className="bg-consortium-green hover:bg-consortium-green-hover text-white gap-2" data-testid="nav-add-product">
                    <Plus className="w-4 h-4" />
                    Add Product
                  </Button>
                </Link>
              </>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="px-3 py-2 border-b">
                  <p className="font-semibold">Notifications</p>
                </div>
                {notifications.length > 0 ? (
                  notifications.map((notif) => (
                    <DropdownMenuItem
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className="cursor-pointer p-3"
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex flex-col gap-1">
                        <p className={`text-sm ${!notif.read ? 'font-semibold' : ''}`}>{notif.content}</p>
                        <p className="text-xs text-slate-500">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-slate-500">
                    No notifications
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu">
                  <div className="w-8 h-8 bg-consortium-gold rounded-full flex items-center justify-center text-white font-semibold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden md:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-3 py-2">
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <Badge className="mt-1">{user?.role}</Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};
